import { useState, useEffect, useMemo } from "react";
import {
  Stack,
  Title,
  Text,
  Group,
  ActionIcon,
  Button,
  Table,
  Badge,
  Alert,
  Pagination,
  Tooltip,
  Paper,
  SegmentedControl,
  Grid,
  Select,
  Loader,
  Modal,
  Divider,
  Code,
  Popover,
  NumberInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconClockPause,
  IconFileInfo,
  IconInfoCircle,
  IconPlayerPlay,
  IconTable,
  IconLayoutGrid,
  IconClockPlay,
  IconBell,
  IconBellOff,
  IconStarOff,
  IconApps,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import useApi from "../utils/api/useApi";
import coreApi from "../utils/api/coreApi";
import {
  Chat,
  ChatStateType,
  HealthCheck,
  HealthCheckRun,
} from "../utils/api/api_types";
import {
  ChatStateNameAndEmoji,
  HealthCheckRunStatusColors,
} from "../utils/enum_records";
import LinkChatModal from "./LinkChatModal";
import { Link, useNavigate } from "react-router";
import { MOBILE_VIEW_MAX_WIDTH_EM } from "../Settings";
import { useMediaQuery } from "@mantine/hooks";
import { STANDARD_ERROR_MESSAGE } from "../utils/constants";
import {
  toggleEnableHealthCheck,
  toggleSubscribeHealthCheck,
  runHealthCheck,
  setAutoPassHealthCheck,
  getPrettyCheckName,
} from "../utils/health_checks";
import { useGlobalContext } from "../context/globalContext";
import { formatDate } from "../utils/dates";

type LoadingHealthCheckStates = {
  loadingPause: Record<number, boolean>;
  loadingSubscribe: Record<number, boolean>;
  loadingRun: Record<number, boolean>;
  loadingLatestLog: Record<number, boolean>;
  loadingAutoPass: Record<number, boolean>;
  loadingRunAll: boolean;
};

type HealthCheckGroup = {
  groupName: string;
  checks: HealthCheck[];
};

const getRunDetailText = (detail: unknown) => JSON.stringify(detail, null, 2);

const getRunDetailModalSize = (detailText: string, isMobile: boolean) => {
  if (isMobile) return undefined;
  if (detailText.length > 12000) return 1440;
  if (detailText.length > 5000) return 1280;
  return 1100;
};

const getAutoPassRemainingText = (autoPassUntil: string | null) => {
  if (!autoPassUntil) return null;

  const remainingMs = new Date(autoPassUntil).getTime() - Date.now();
  if (remainingMs <= 0) return "Expired";

  const totalMinutes = Math.ceil(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `${minutes}m left`;
  if (minutes === 0) return `${hours}h left`;
  return `${hours}h ${minutes}m left`;
};

export default function Dashboard() {
  const { latestEvent } = useGlobalContext();

  const isMobile = useMediaQuery(`(max-width: ${MOBILE_VIEW_MAX_WIDTH_EM})`);
  const [viewMode, setViewMode] = useState<
    "table_all" | "grid_all" | "grid_app" | null
  >(null);

  // Wait until media query is defined before setting initial view
  useEffect(() => {
    if (viewMode === null && typeof isMobile === "boolean") {
      setViewMode("grid_app");
      return;
    }

    // Enforce a grid view on mobile if the user was on the table view
    if (isMobile === true && viewMode === "table_all") {
      setViewMode("grid_app");
    }
  }, [isMobile, viewMode]);

  // Getting & Refreshing HealthCheck data
  const healthChecksRes = useApi<HealthCheck[]>({
    apiFunc: coreApi.healthChecksList,
    defaultData: [],
    paginated: true,
  });

  const [filterBy, setFilterBy] = useState<string>("null");
  const [healthChecksData, setHealthChecksData] = useState<HealthCheck[]>(
    healthChecksRes.responseData.data
  );
  const [hasRequestedHealthChecks, setHasRequestedHealthChecks] = useState(false);
  const [hasLoadedHealthChecks, setHasLoadedHealthChecks] = useState(false);

  useEffect(() => {
    if (healthChecksRes.loading) {
      setHasRequestedHealthChecks(true);
      return;
    }

    if (hasRequestedHealthChecks) {
      setHasLoadedHealthChecks(true);
    }
  }, [hasRequestedHealthChecks, healthChecksRes.loading]);

  // Handles loading data for different views
  // table_all -> user changes pages so we just replace data
  // grid_all -> user loades more data so we append
  // grid_app -> requires all data, so we load automatically
  // TODO: REFACTOR
  const [allLoaded, setAllLoaded] = useState(false);
  useEffect(() => {
    if (!viewMode) return;

    // On first page replace data entirely - happens when switch mode
    if (healthChecksRes.responseData.metaData.page === 1) {
      setHealthChecksData(healthChecksRes.responseData.data);
      if (viewMode !== "grid_app") return;
    }

    if (viewMode === "table_all") {
      // Replace data entirely
      setHealthChecksData(healthChecksRes.responseData.data);
    } else {
      // Merge (load more)
      setHealthChecksData((existingData) => {
        const combined = [
          ...existingData,
          ...healthChecksRes.responseData.data,
        ];

        const deduped = combined.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        );

        return deduped;
      });
      // If grid_app, we keep loading until end
      if (viewMode === "grid_app") {
        if (
          healthChecksRes.responseData.metaData.page <
          healthChecksRes.responseData.metaData.total_pages
        ) {
          healthChecksRes.setParams((params) => ({
            ...params,
            page: healthChecksRes.responseData.metaData.page + 1,
          }));
        } else {
          setAllLoaded(true);
        }
      }
    }
  }, [viewMode, JSON.stringify(healthChecksRes.responseData)]);
  useEffect(() => {
    setFilterBy(healthChecksRes.responseData.metaData.current_filter || "null");
  }, [JSON.stringify(healthChecksRes.responseData.metaData.current_filter)]);

  // If there was an update in the health check that is on the current page - refresh
  useEffect(() => {
    if (!latestEvent) return;

    const healthCheckId = latestEvent["health_check_id"];
    const exists = healthChecksData.some((item) => item.id === healthCheckId);
    if (exists) healthChecksRes.refresh();
  }, [JSON.stringify(latestEvent)]);

  // TODO: Should be refactored too?
  const [loadingStates, setLoadingStates] = useState<LoadingHealthCheckStates>({
    loadingPause: {},
    loadingSubscribe: {},
    loadingRun: {},
    loadingLatestLog: {},
    loadingAutoPass: {},
    loadingRunAll: false,
  });
  const [selectedHealthCheckRun, setSelectedHealthCheckRun] =
    useState<HealthCheckRun | null>(null);
  const selectedHealthCheckRunDetailText =
    selectedHealthCheckRun === null
      ? ""
      : getRunDetailText(selectedHealthCheckRun.detail);

  const updateHealthCheckInState = (updatedHealthCheck: HealthCheck) => {
    setHealthChecksData((prevHealthChecks) =>
      prevHealthChecks.map((item) =>
        item.id === updatedHealthCheck.id ? updatedHealthCheck : item
      )
    );
  };

  const handleToggleEnableHealthCheck = async (id: number) => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingPause: { ...prev.loadingPause, [id]: true },
      }));

      const healthCheck = await toggleEnableHealthCheck(id);
      updateHealthCheckInState(healthCheck);
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        loadingPause: { ...prev.loadingPause, [id]: false },
      }));
    }
  };

  const handleToggleSubscribeHealthCheck = async (id: number) => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingSubscribe: { ...prev.loadingSubscribe, [id]: true },
      }));

      const healthCheck = await toggleSubscribeHealthCheck(id);
      updateHealthCheckInState(healthCheck);
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        loadingSubscribe: { ...prev.loadingSubscribe, [id]: false },
      }));
    }
  };

  const handleTriggerHealthCheck = async (id: number) => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingRun: { ...prev.loadingRun, [id]: true },
      }));

      const healthCheck = await runHealthCheck(id);
      updateHealthCheckInState(healthCheck);
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        loadingRun: { ...prev.loadingRun, [id]: false },
      }));
    }
  };

  const handleOpenLatestRun = async (healthCheckId: number) => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingLatestLog: {
          ...prev.loadingLatestLog,
          [healthCheckId]: true,
        },
      }));

      const response = await coreApi.healthChecksRuns(healthCheckId.toString(), {
        params: { page: 1 },
      });
      const latestRun = response.data?.[0];

      if (!latestRun) {
        notifications.show({
          title: "No recent run",
          message: "This health check does not have any runs yet.",
          color: "gray",
        });
        return;
      }

      setSelectedHealthCheckRun(latestRun);
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        loadingLatestLog: {
          ...prev.loadingLatestLog,
          [healthCheckId]: false,
        },
      }));
    }
  };

  const handleSetAutoPass = async (id: number, hours: number | null) => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingAutoPass: { ...prev.loadingAutoPass, [id]: true },
      }));

      const healthCheck = await setAutoPassHealthCheck(id, hours);
      updateHealthCheckInState(healthCheck);
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        loadingAutoPass: { ...prev.loadingAutoPass, [id]: false },
      }));
    }
  };

  const fetchAllFilteredHealthChecks = async () => {
    const filterParams =
      filterBy !== "null"
        ? {
            [filterBy]: true,
          }
        : {};

    const firstPage = await coreApi.healthChecksList({
      params: {
        ...filterParams,
        page: 1,
      },
    });

    const allChecks = [...(firstPage.data || [])];
    const totalPages = firstPage.meta_data?.total_pages || 1;

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await coreApi.healthChecksList({
        params: {
          ...filterParams,
          page,
        },
      });
      allChecks.push(...(response.data || []));
    }

    return allChecks;
  };

  const handleRunAllHealthChecks = async () => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingRunAll: true,
      }));

      const allChecks = await fetchAllFilteredHealthChecks();
      if (allChecks.length === 0) {
        notifications.show({
          title: "No health checks",
          message: "There are no health checks in the current view.",
          color: "gray",
        });
        return;
      }

      for (const healthCheck of allChecks) {
        await coreApi.healthChecksManualTrigger(healthCheck.id);
      }

      notifications.show({
        title: "Run all started",
        message: `Started ${allChecks.length} health checks.`,
        color: "green",
      });
      healthChecksRes.refresh();
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        loadingRunAll: false,
      }));
    }
  };

  const handlePauseAllHealthChecks = async () => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        loadingPause: { ...prev.loadingPause },
      }));

      const allChecks = await fetchAllFilteredHealthChecks();
      const checksToPause = allChecks.filter(
        (healthCheck) => healthCheck.schedule && healthCheck.enabled
      );

      if (checksToPause.length === 0) {
        notifications.show({
          title: "Nothing to pause",
          message: "There are no enabled scheduled health checks in the current view.",
          color: "gray",
        });
        return;
      }

      for (const healthCheck of checksToPause) {
        setLoadingStates((prev) => ({
          ...prev,
          loadingPause: { ...prev.loadingPause, [healthCheck.id]: true },
        }));
        const updatedHealthCheck = await toggleEnableHealthCheck(healthCheck.id);
        updateHealthCheckInState(updatedHealthCheck);
        setLoadingStates((prev) => ({
          ...prev,
          loadingPause: { ...prev.loadingPause, [healthCheck.id]: false },
        }));
      }

      notifications.show({
        title: "Pause all complete",
        message: `Paused ${checksToPause.length} health checks.`,
        color: "yellow",
      });
      healthChecksRes.refresh();
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    }
  };

  const handleSubscribeAllHealthChecks = async () => {
    try {
      const allChecks = await fetchAllFilteredHealthChecks();
      const checksToSubscribe = allChecks.filter(
        (healthCheck) => !healthCheck.subscribed
      );

      if (checksToSubscribe.length === 0) {
        notifications.show({
          title: "Nothing to subscribe",
          message: "All health checks in the current view are already subscribed.",
          color: "gray",
        });
        return;
      }

      for (const healthCheck of checksToSubscribe) {
        setLoadingStates((prev) => ({
          ...prev,
          loadingSubscribe: {
            ...prev.loadingSubscribe,
            [healthCheck.id]: true,
          },
        }));
        const updatedHealthCheck = await toggleSubscribeHealthCheck(
          healthCheck.id
        );
        updateHealthCheckInState(updatedHealthCheck);
        setLoadingStates((prev) => ({
          ...prev,
          loadingSubscribe: {
            ...prev.loadingSubscribe,
            [healthCheck.id]: false,
          },
        }));
      }

      notifications.show({
        title: "Subscribe all complete",
        message: `Subscribed to ${checksToSubscribe.length} health checks.`,
        color: "green",
      });
      healthChecksRes.refresh();
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    }
  };

  const helathCheckRows = healthChecksData.map((healthCheck, i) => (
    <Table.Tr key={i}>
      <Table.Td>{healthCheck.name.replace(/_/g, " ")}</Table.Td>
      <Table.Td>{healthCheck.app.name.replace(/_/g, " ")}</Table.Td>
      <Table.Td>
        {healthCheck.schedule?.schedule_display
          ? healthCheck.schedule?.schedule_display
          : "No Schedule"}
      </Table.Td>
      <Table.Td>
        {healthCheck?.schedule ? (
          healthCheck.enabled === true ? (
            healthCheck.schedule.next_run_formatted
          ) : (
            <Badge color="yellow">Paused</Badge>
          )
        ) : (
          ""
        )}
      </Table.Td>
      <Table.Td>
        <Badge
          color={
            healthCheck.latest_status
              ? HealthCheckRunStatusColors[healthCheck.latest_status]
              : "gray"
          }
          variant="light"
        >
          {healthCheck.latest_status
            ? healthCheck.latest_status.replace(/_/g, " ")
            : "NO RUNS"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group>
          <Tooltip label={healthCheck.subscribed ? "Unsubscribe" : "Subscribe"}>
            <ActionIcon
              variant="light"
              color={healthCheck.subscribed ? "red" : "indigo"}
              onClick={() => handleToggleSubscribeHealthCheck(healthCheck.id)}
              loading={loadingStates.loadingSubscribe[healthCheck.id]}
            >
              {healthCheck.subscribed ? <IconBellOff /> : <IconBell />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Trigger Manually">
            <ActionIcon
              variant="light"
              color="teal"
              onClick={() => handleTriggerHealthCheck(healthCheck.id)}
              loading={loadingStates.loadingRun[healthCheck.id]}
            >
              <IconPlayerPlay />
            </ActionIcon>
          </Tooltip>
          {healthCheck.schedule && (
            <Tooltip
              label={`${healthCheck.enabled ? "Pause" : "Resume"} Schedule`}
            >
              <ActionIcon
                variant="light"
                color={healthCheck.enabled ? "yellow" : "green"}
                onClick={() => handleToggleEnableHealthCheck(healthCheck.id)}
                loading={loadingStates.loadingPause[healthCheck.id]}
              >
                {healthCheck.enabled ? <IconClockPause /> : <IconClockPlay />}
              </ActionIcon>
            </Tooltip>
          )}
          <Link
            to={`health-check/${healthCheck.id}`}
            style={{ textDecoration: "none" }}
          >
            <Tooltip label="View Detail">
              <ActionIcon variant="light">
                <IconFileInfo />
              </ActionIcon>
            </Tooltip>
          </Link>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  // Chat linking - TODO: Refactor all Phone linking logic into separate component

  const [
    openedChatLinkModal,
    { open: openChatLinkModal, close: closeChatLinkModal },
  ] = useDisclosure(false);

  const chatsRes = useApi<Chat[]>({
    apiFunc: coreApi.chatsList,
    defaultData: [],
    paginated: false,
  });
  const chatsData = chatsRes.responseData.data;

  // TODO: Will not work with multiple chats
  const oneChat = chatsData.length > 0 ? chatsData[0] : null;

  const disabledNotificaitonsContent =
    oneChat && oneChat.state != ChatStateType.ACTIVE ? (
      <Group justify="space-between">
        <Text>⚠️ Warning Status:</Text>
        <Text>{ChatStateNameAndEmoji[oneChat.state]}</Text>
      </Group>
    ) : oneChat === null ? (
      <Group justify="space-between">
        <Text>📵 No Phone Linked</Text>
        <LinkPhoneButton openChatLinkModal={openChatLinkModal} />
      </Group>
    ) : null;

  const segmentOptions = [
    {
      value: "table_all",
      label: (
        <Group gap={4} wrap="nowrap">
          <IconTable size={16} /> <Text size="sm">Table All</Text>
        </Group>
      ),
    },
    {
      value: "grid_all",
      label: (
        <Group gap={4} wrap="nowrap">
          <IconLayoutGrid size={16} /> <Text size="sm">Grid All</Text>
        </Group>
      ),
    },
    {
      value: "grid_app",
      label: (
        <Group gap={4} wrap="nowrap">
          <IconApps size={16} /> <Text size="sm">Grid By App</Text>
        </Group>
      ),
    },
  ];

  const segmentOptionsFiltered = useMemo(() => {
    if (isMobile) {
      return segmentOptions.filter((opt) => opt.value !== "table_all");
    }
    return segmentOptions;
  }, [isMobile]);

  const showInitialHealthChecksLoader =
    !hasLoadedHealthChecks && healthChecksRes.loading;

  return (
    <>
      {selectedHealthCheckRun && (
        <Modal
          opened={selectedHealthCheckRun != null}
          centered
          size={getRunDetailModalSize(
            selectedHealthCheckRunDetailText,
            Boolean(isMobile)
          )}
          onClose={() => {
            setSelectedHealthCheckRun(null);
          }}
          title="Health Check Run Detail"
        >
          <Stack>
            <Group justify="space-between" align="flex-start">
              <div>
                <Text>
                  <b>Started:</b> {formatDate(selectedHealthCheckRun.created_at)}
                </Text>
                <Text>
                  <b>Finished: </b>
                  {formatDate(selectedHealthCheckRun.completed_at)}
                </Text>
                <Text>
                  <b>Triggered By: </b>
                  {selectedHealthCheckRun.triggered_by_user?.username ||
                    "🤖 AUTO"}
                </Text>
              </div>
              <Badge
                color={HealthCheckRunStatusColors[selectedHealthCheckRun.status]}
                variant="light"
              >
                {selectedHealthCheckRun.status.replace(/_/g, " ")}
              </Badge>
            </Group>
            <Divider />
            <Code
              block
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: isMobile ? undefined : "70vh",
                overflow: "auto",
              }}
            >
              {selectedHealthCheckRunDetailText}
            </Code>
          </Stack>
        </Modal>
      )}

      <LinkChatModal
        opened={openedChatLinkModal}
        onClose={closeChatLinkModal}
        onSuccess={chatsRes.refresh}
      />

      {/* Main Content */}
      <Stack gap="lg">
        {chatsRes.loading ? (
          <></>
        ) : (
          <Stack w="100%" align="center">
            {disabledNotificaitonsContent ? (
              <Alert
                mt="xl"
                variant="light"
                color="orange"
                title="Not receiving alerts"
                icon={<IconInfoCircle />}
              >
                {disabledNotificaitonsContent}
              </Alert>
            ) : (
              <></>
            )}
          </Stack>
        )}

        <Title order={isMobile ? 3 : 2}>Health Checks</Title>

        <Group mb="md">
          {viewMode ? (
            <SegmentedControl
              value={viewMode}
              onChange={(val) => {
                setAllLoaded(false);
                healthChecksRes.setParams((params) => ({ ...params, page: 1 }));
                setViewMode(val as "table_all" | "grid_all" | "grid_app");
              }}
              data={segmentOptionsFiltered}
            />
          ) : (
            <></>
          )}
          <Button
            variant="light"
            color="teal"
            loading={loadingStates.loadingRunAll}
            onClick={handleRunAllHealthChecks}
            disabled={healthChecksRes.loading}
          >
            Run all
          </Button>
          <Button
            variant="light"
            color="yellow"
            onClick={handlePauseAllHealthChecks}
            disabled={healthChecksRes.loading}
          >
            Pause all
          </Button>
          <Button
            variant="light"
            color="indigo"
            onClick={handleSubscribeAllHealthChecks}
            disabled={healthChecksRes.loading}
          >
            Subscribe all
          </Button>
          {healthChecksRes.responseData.metaData.available_filters && (
            <Select
              data={[
                { value: "null", label: "No Filter" },
                ...Object.entries(
                  healthChecksRes.responseData.metaData.available_filters
                ).map(([key, value]) => ({ value: key, label: value })),
              ]}
              value={filterBy}
              onChange={(value, _) => {
                if (!value) return;
                setAllLoaded(false);
                if (value !== "null") {
                  healthChecksRes.setParams((preParams) => ({
                    ...preParams,
                    [value]: true,
                    page: 1,
                  }));
                } else {
                  healthChecksRes.setParams({
                    page: 1,
                  });
                }
              }}
            />
          )}
        </Group>
      </Stack>

      {showInitialHealthChecksLoader ? (
        <Stack
          align="center"
          justify="center"
          style={{ height: "calc(100vh - 210px)" }}
        >
          <Loader type="bars" />
        </Stack>
      ) : healthChecksData.length === 0 && filterBy === "is_favourite" ? (
        <Stack
          align="center"
          justify="center"
          style={{ height: "calc(100vh - 210px)" }}
        >
          <IconStarOff size={50} color="grey" />
          <Text>No Favourites</Text>
          <Link to="manage-apps">
            <Button variant="subtle">Select Favourites</Button>
          </Link>
        </Stack>
      ) : viewMode && ["grid_all", "grid_app"].includes(viewMode) ? (
        <Stack>
          <HealthCheckGrid
            healthChecks={healthChecksData}
            groupByApp={viewMode === "grid_app"}
            allLoaded={allLoaded}
            latestLogLoading={loadingStates.loadingLatestLog}
            pauseLoading={loadingStates.loadingPause}
            subscribeLoading={loadingStates.loadingSubscribe}
            runLoading={loadingStates.loadingRun}
            autoPassLoading={loadingStates.loadingAutoPass}
            onOpenLatestRun={handleOpenLatestRun}
            onToggleSubscribe={handleToggleSubscribeHealthCheck}
            onTriggerHealthCheck={handleTriggerHealthCheck}
            onToggleEnableHealthCheck={handleToggleEnableHealthCheck}
            onSetAutoPass={handleSetAutoPass}
          />
          {viewMode === "grid_all" && healthChecksRes.responseData?.metaData ? (
            healthChecksRes.responseData.metaData.total_pages >
            healthChecksRes.responseData.metaData.page ? (
              <Stack align="center">
                <Button
                  mb={30}
                  variant="subtle"
                  loading={healthChecksRes.loading}
                  onClick={() =>
                    healthChecksRes.setParams((params) => ({
                      ...params,
                      page: healthChecksRes.responseData.metaData.page + 1,
                    }))
                  }
                >
                  Load More
                </Button>
              </Stack>
            ) : (
              <></>
            )
          ) : null}
        </Stack>
      ) : viewMode === "table_all" ? (
        <Stack align="center">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name </Table.Th>
                <Table.Th>Application</Table.Th>
                <Table.Th>Schedule</Table.Th>
                <Table.Th>Next Run</Table.Th>
                <Table.Th>Latest Status</Table.Th>
                {/* TODO: 30d rolling FAILS */}
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{helathCheckRows}</Table.Tbody>
          </Table>
          {healthChecksRes.responseData.metaData &&
          healthChecksRes.responseData.metaData.total_pages > 1 ? (
            <Pagination
              mb={30}
              py="xl"
              value={healthChecksRes.responseData.metaData.page}
              onChange={(p) =>
                healthChecksRes.setParams((params) => ({ ...params, page: p }))
              }
              total={healthChecksRes.responseData.metaData.total_pages}
            />
          ) : (
            <></>
          )}
        </Stack>
      ) : (
        <></>
      )}
    </>
  );
}

const HealthCheckGrid = ({
  healthChecks,
  groupByApp,
  allLoaded,
  latestLogLoading,
  pauseLoading,
  subscribeLoading,
  runLoading,
  autoPassLoading,
  onOpenLatestRun,
  onToggleSubscribe,
  onTriggerHealthCheck,
  onToggleEnableHealthCheck,
  onSetAutoPass,
}: {
  healthChecks: HealthCheck[];
  groupByApp: boolean;
  allLoaded: boolean;
  latestLogLoading: Record<number, boolean>;
  pauseLoading: Record<number, boolean>;
  subscribeLoading: Record<number, boolean>;
  runLoading: Record<number, boolean>;
  autoPassLoading: Record<number, boolean>;
  onOpenLatestRun: (healthCheckId: number) => void;
  onToggleSubscribe: (healthCheckId: number) => void;
  onTriggerHealthCheck: (healthCheckId: number) => void;
  onToggleEnableHealthCheck: (healthCheckId: number) => void;
  onSetAutoPass: (healthCheckId: number, hours: number | null) => Promise<void>;
}) => {
  const navigate = useNavigate();
  const [pressedId, setPressedId] = useState<number | null>(null);

  const handlePress = (id: number) => setPressedId(id);
  const handleRelease = () => setPressedId(null);

  const groupedEntries: HealthCheckGroup[] = useMemo((): HealthCheckGroup[] => {
    if (!groupByApp) {
      return [{ groupName: "All", checks: healthChecks }];
    }

    const grouped = healthChecks.reduce((acc, healthCheck) => {
      const appName = healthCheck.app.name;
      if (!acc[appName]) acc[appName] = [];
      acc[appName].push(healthCheck);
      return acc;
    }, {} as Record<string, HealthCheck[]>);

    return Object.entries(grouped)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([groupName, checks]) => ({
        groupName,
        checks: [...checks].sort((left, right) =>
          left.name.localeCompare(right.name)
        ),
      }));
  }, [groupByApp, healthChecks]);

  return (
    <Stack>
      {groupByApp && !allLoaded ? (
        <Stack
          align="center"
          justify="center"
          style={{ height: "calc(100vh - 210px)" }}
        >
          <Loader type="bars" />
        </Stack>
      ) : (
        groupedEntries.map((group: HealthCheckGroup) => {
          const { groupName, checks } = group;

          return (
            <Paper
              key={groupName}
              withBorder
              radius="lg"
              p="md"
              shadow="xs"
              style={{
                borderColor: "var(--mantine-color-gray-4)",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Stack gap="sm">
                {groupByApp && (
                  <Title order={5}>{groupName.replace(/_/g, " ")}</Title>
                )}

                <Grid
                  justify="flex-start"
                  align="flex-start"
                  overflow="hidden"
                  breakpoints={{
                    xs: "200px",
                    sm: "300px",
                    md: "400px",
                    lg: "500px",
                    xl: "600px",
                  }}
                  gutter={8}
                  w="100%"
                >
                  {checks.map((healthCheck: HealthCheck) => {
                    const color = healthCheck.latest_status
                      ? HealthCheckRunStatusColors[healthCheck.latest_status]
                      : "gray";

                    const isPressed = pressedId === healthCheck.id;

                    return (
                      <Grid.Col key={healthCheck.id} span={{ base: 12, sm: 6, xl: 3 }}>
                        <Paper
                          shadow="sm"
                          p="md"
                          radius="md"
                          withBorder
                          onMouseDown={() => handlePress(healthCheck.id)}
                          onMouseUp={handleRelease}
                          onMouseLeave={handleRelease}
                          onTouchStart={() => handlePress(healthCheck.id)}
                          onTouchEnd={handleRelease}
                          onClick={() => navigate(`health-check/${healthCheck.id}`)}
                          style={{
                            backgroundColor: `var(--mantine-color-${color}-3)`,
                            cursor: "pointer",
                            transform: isPressed ? "scale(0.97)" : "scale(1)",
                            transition: "transform 0.15s, box-shadow 0.15s",
                            boxShadow: isPressed
                              ? "0 2px 6px rgba(0,0,0,0.08)"
                              : "0 10px 20px rgba(0,0,0,0.12)",
                          }}
                        >
                          <Stack gap="xs">
                          <div
                            style={{
                              position: "relative",
                              minHeight: 88,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Stack
                              align="center"
                              gap="xs"
                              style={{
                                paddingLeft: 40,
                                paddingRight: 40,
                              }}
                            >
                              <Text fw={600} lineClamp={4} ta="center">
                                {groupByApp
                                  ? healthCheck.name.replace(/_/g, " ")
                                  : getPrettyCheckName(
                                      healthCheck.app.name,
                                      healthCheck.name
                                    )}
                              </Text>
                              <Text size="sm">{healthCheck.last_run_time}</Text>
                            </Stack>
                            <div
                              style={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            >
                              <AutoPassAction
                                healthCheck={healthCheck}
                                loading={autoPassLoading[healthCheck.id]}
                                onSetAutoPass={onSetAutoPass}
                              />
                            </div>
                            <Stack
                              gap={4}
                              style={{
                                position: "absolute",
                                right: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            >
                              <Tooltip
                                label={
                                  healthCheck.subscribed
                                    ? "Unsubscribe"
                                    : "Subscribe"
                                }
                              >
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color={healthCheck.subscribed ? "red" : "indigo"}
                                  loading={subscribeLoading[healthCheck.id]}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onToggleSubscribe(healthCheck.id);
                                  }}
                                >
                                  {healthCheck.subscribed ? (
                                    <IconBellOff size={16} />
                                  ) : (
                                    <IconBell size={16} />
                                  )}
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Trigger Manually">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="teal"
                                  loading={runLoading[healthCheck.id]}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onTriggerHealthCheck(healthCheck.id);
                                  }}
                                >
                                  <IconPlayerPlay size={16} />
                                </ActionIcon>
                              </Tooltip>
                              {healthCheck.schedule && (
                                <Tooltip
                                  label={`${
                                    healthCheck.enabled ? "Pause" : "Resume"
                                  } Schedule`}
                                >
                                  <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color={
                                      healthCheck.enabled ? "yellow" : "green"
                                    }
                                    loading={pauseLoading[healthCheck.id]}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      onToggleEnableHealthCheck(healthCheck.id);
                                    }}
                                  >
                                    {healthCheck.enabled ? (
                                      <IconClockPause size={16} />
                                    ) : (
                                      <IconClockPlay size={16} />
                                    )}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                              <Tooltip label="Latest Log">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="dark"
                                  loading={latestLogLoading[healthCheck.id]}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onOpenLatestRun(healthCheck.id);
                                  }}
                                >
                                  <IconFileInfo size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Stack>
                          </div>
                          <Stack align="center" gap="xs">
                            {healthCheck.schedule == null ? (
                              <Badge color="red">No Schedule</Badge>
                            ) : healthCheck.enabled === false ? (
                              <Badge color="yellow">Paused</Badge>
                            ) : (
                              <></>
                            )}

                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Stack>
          </Paper>
          );
        })
      )}
    </Stack>
  );
};

function AutoPassAction({
  healthCheck,
  loading,
  onSetAutoPass,
}: {
  healthCheck: HealthCheck;
  loading?: boolean;
  onSetAutoPass: (healthCheckId: number, hours: number | null) => Promise<void>;
}) {
  const [opened, setOpened] = useState(false);
  const [hours, setHours] = useState<number>(3);
  const remainingText = getAutoPassRemainingText(healthCheck.auto_pass_until);

  const submit = async (hoursValue: number | null) => {
    await onSetAutoPass(healthCheck.id, hoursValue);
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="right"
      width={230}
      withArrow
      shadow="md"
    >
      <Popover.Target>
        <Tooltip
          label={healthCheck.auto_pass_active ? "Auto-pass active" : "Auto-pass"}
          disabled={opened}
        >
          <ActionIcon
            size="sm"
            variant="subtle"
            color={healthCheck.auto_pass_active ? "green" : "dark"}
            loading={loading}
            onClick={(event) => {
              event.stopPropagation();
              setOpened((current) => !current);
            }}
          >
            <IconShieldCheck size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Auto-pass
          </Text>
          {healthCheck.auto_pass_active ? (
            <Text size="xs" c="dimmed">
              {remainingText}
              {healthCheck.auto_pass_until_formatted
                ? `, until ${healthCheck.auto_pass_until_formatted}`
                : ""}
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              Temporarily force this check to pass.
            </Text>
          )}
          <NumberInput
            label="Hours"
            size="xs"
            min={1}
            max={168}
            allowDecimal={false}
            value={hours}
            onChange={(value) =>
              setHours(typeof value === "number" && value > 0 ? value : 1)
            }
          />
          <Group grow>
            <Button
              size="xs"
              onClick={() => {
                void submit(hours);
              }}
              loading={loading}
            >
              Apply
            </Button>
            {healthCheck.auto_pass_active ? (
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={() => {
                  void submit(null);
                }}
                loading={loading}
              >
                Remove
              </Button>
            ) : null}
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

function LinkPhoneButton({
  openChatLinkModal,
}: {
  openChatLinkModal: () => void;
}) {
  return (
    <Button onClick={openChatLinkModal} variant="filled" color="orange">
      Link Phone
    </Button>
  );
}
